import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, Check } from "lucide-react";
import { api } from "@/lib/api";

interface Titular {
  id: string;
  razonSocial: string;
  rfc: string;
  estatus: string;
}

interface PageResp<T> {
  content: T[];
  totalElements: number;
}

/**
 * Combobox para seleccionar un registro legal con bandera TITULAR.
 * Espejo de {@link TramitadorPicker} pero con `bandera=TITULAR`. Mantenido
 * separado por simplicidad — refactorizar a un picker genérico cuando se
 * necesite un tercer caso.
 */
export function TitularPicker({
  value,
  onChange,
}: {
  value: Titular | null;
  onChange: (t: Titular | null) => void;
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const query = useQuery({
    queryKey: ["titulares", debounced],
    queryFn: async () => {
      const params = new URLSearchParams({ bandera: "TITULAR", size: "20", sort: "razonSocial" });
      // Solo RLs operables: cancelados/suspendidos quedan para consulta, no para trámites nuevos.
      params.append("estatus", "APROBADO");
      params.append("estatus", "ACTIVO");
      if (debounced) params.set("q", debounced);
      const { data } = await api.get<PageResp<Titular>>(`/api/registros-legales?${params.toString()}`);
      return data;
    },
    enabled: open,
  });

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded border bg-estado-ok-bg px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-estado-ok" />
          <div>
            <div className="font-medium text-fg">{value.razonSocial}</div>
            <div className="font-mono text-xs text-fg-subtle">{value.rfc}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="rounded p-1 text-fg-subtle hover:bg-surface-2"
          aria-label="Quitar titular"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="flex items-center gap-2 rounded border bg-surface px-2">
        <Search className="h-4 w-4 text-fg-faint" />
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar titular por razón social o RFC…"
          className="flex-1 border-0 bg-transparent px-1 py-1.5 text-sm focus:outline-none"
        />
      </div>
      {open && (
        <div className="absolute left-0 right-0 z-10 mt-1 max-h-64 overflow-auto rounded-lg border bg-surface shadow-overlay">
          {query.isLoading && (
            <div className="p-3 text-xs text-fg-subtle">Buscando…</div>
          )}
          {query.error && (
            <div className="p-3 text-xs text-estado-err">Error consultando titulares.</div>
          )}
          {query.data && (
            <>
              {query.data.content.length === 0 ? (
                <div className="p-3 text-xs text-fg-subtle">
                  Sin resultados. Solo aparecen registros con la bandera Titular.
                </div>
              ) : (
                <ul className="divide-y text-sm">
                  {query.data.content.map((t) => (
                    <li
                      key={t.id}
                      onClick={() => {
                        onChange(t);
                        setOpen(false);
                        setQ("");
                      }}
                      className="cursor-pointer px-3 py-2 hover:bg-estado-info-bg"
                    >
                      <div className="font-medium text-fg">{t.razonSocial}</div>
                      <div className="font-mono text-xs text-fg-subtle">{t.rfc}</div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
